import React, { useState } from 'react';
import { TranscriptTurn } from '../types';
import { useTranslation } from '../../../i18n';

interface TranscriptViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  transcript: TranscriptTurn[];
  scenarioTitle?: string | null;
  personaName?: string | null;
}

export const TranscriptViewerModal: React.FC<TranscriptViewerModalProps> = ({
  isOpen,
  onClose,
  transcript = [],
  scenarioTitle,
  personaName,
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopyTranscript = () => {
    const text = transcript
      .map((t, idx) => {
        const rawRole = (((t as any).role || '') + '').toLowerCase();
        const rawSpeaker = ((t.speaker || '') + '').toLowerCase();
        const isLearner = ['learner', 'user', 'me', 'student'].includes(rawSpeaker) || ['learner', 'user'].includes(rawRole);
        const speaker = isLearner
          ? (t.speakerName || 'Learner')
          : (t.speakerName || (t as any).personaName || personaName || 'Prakash Mehta');
        const cleanT = (t.text || (t as any).content || '')
          .replace(/^(CUSTOMER|LEARNER|AI|USER|PERSONA)\s*(\([^)]*\))?\s*(SAID|SAYS)?\s*:\s*/i, '')
          .replace(/^\[(CUSTOMER|LEARNER|AI|USER|PERSONA|[^\]]+)\]\s*:\s*/i, '')
          .replace(/^["']|["']$/g, '')
          .trim();
        return `[Turn #${t.turnNumber || idx + 1}] ${speaker}:\n${cleanT}\n`;
      })
      .join('\n');

    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="admin-modal-overlay" style={{ zIndex: 1000 }}>
      <div className="admin-modal" style={{ maxWidth: '720px', width: '92vw', borderRadius: '16px', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--line)', padding: '18px 24px', background: '#F8FAFC' }}>
          <div>
            <h3 style={{ fontFamily: 'var(--display)', fontSize: '18px', fontWeight: 700, color: 'var(--navy)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>📜</span> {t('scorecard.modal_transcript_title', undefined, 'Dialogue Communication Transcript')}
            </h3>
            <p style={{ fontSize: '12.5px', color: 'var(--muted)', margin: '3px 0 0' }}>
              {scenarioTitle || t('scorecard.modal_transcript_sub', undefined, 'Session Transcript')} • <strong>{t('scorecard.total_turns_count', { count: transcript.length }, `${transcript.length} turns recorded`)}</strong>
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              onClick={handleCopyTranscript}
              className="admin-btn admin-btn--secondary admin-btn--sm"
              style={{ fontSize: '12px', padding: '6px 12px' }}
              title="Copy entire transcript text"
            >
              {copied ? t('scorecard.transcript_copied', undefined, '✓ Copied') : t('scorecard.copy_transcript_text', undefined, '📋 Copy Text')}
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{ background: 'none', border: 'none', fontSize: '20px', color: 'var(--muted)', cursor: 'pointer', padding: '4px 8px', lineHeight: 1 }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Scrollable conversation body */}
        <div style={{ maxHeight: '60vh', overflowY: 'auto', display: 'grid', gap: '12px', padding: '20px 24px', background: '#FFFFFF' }}>
          {transcript.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '36px 0', color: 'var(--muted)' }}>
              <span style={{ fontSize: '32px', display: 'block', marginBottom: '8px' }}>💬</span>
              <p style={{ fontSize: '14px', margin: 0, fontWeight: 500 }}>No dialogue transcript recorded for this attempt.</p>
              <p style={{ fontSize: '12px', margin: '4px 0 0', color: '#94A3B8' }}>Utterances will appear here once audio or text exchanges occur.</p>
            </div>
          ) : (
            transcript.map((turn, idx) => {
              const rawRole = (((turn as any).role || '') + '').toLowerCase();
              const rawSpeaker = ((turn.speaker || '') + '').toLowerCase();
              const isLearner = ['learner', 'user', 'me', 'student'].includes(rawSpeaker) || ['learner', 'user'].includes(rawRole);
              const rawText = turn.text || (turn as any).content || (turn as any).message || '';
              const turnText = rawText
                .replace(/^(CUSTOMER|LEARNER|AI|USER|PERSONA)\s*(\([^)]*\))?\s*(SAID|SAYS)?\s*:\s*/i, '')
                .replace(/^\[(CUSTOMER|LEARNER|AI|USER|PERSONA|[^\]]+)\]\s*:\s*/i, '')
                .replace(/^["']|["']$/g, '')
                .trim();
              const speakerLabel = isLearner
                ? (turn.speakerName || 'Learner')
                : (turn.speakerName || (turn as any).personaName || personaName || 'Prakash Mehta');

              return (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignSelf: isLearner ? 'flex-end' : 'flex-start',
                    maxWidth: '88%',
                    width: '100%',
                  }}
                >
                  <div
                    style={{
                      padding: '14px 16px',
                      borderRadius: isLearner ? '14px 14px 2px 14px' : '14px 14px 14px 2px',
                      border: isLearner ? '1px solid #BFDBFE' : '1px solid #E2E8F0',
                      background: isLearner ? '#EFF6FF' : '#F8FAFC',
                      fontSize: '13.5px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11.5px', fontWeight: 700, marginBottom: '6px' }}>
                      <span style={{ color: isLearner ? '#1D4ED8' : '#0F766E', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span>{isLearner ? '👤' : '🤖'}</span>
                        {speakerLabel}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: '#94A3B8', fontSize: '11px', fontWeight: 500 }}>
                          Turn #{turn.turnNumber || idx + 1}
                        </span>
                        {!isLearner && turnText && (
                          <button
                            type="button"
                            onClick={() => {
                              if (typeof window !== 'undefined' && window.speechSynthesis) {
                                window.speechSynthesis.cancel();
                                const u = new SpeechSynthesisUtterance(turnText);
                                u.rate = 1.0;
                                window.speechSynthesis.speak(u);
                              }
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '2px 4px',
                              fontSize: '13px',
                              color: '#64748B',
                            }}
                            title="Listen to this line"
                          >
                            🔊
                          </button>
                        )}
                      </div>
                    </div>
                    <p style={{ margin: 0, color: '#1E293B', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
                      {turnText}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div style={{ borderTop: '1px solid var(--line)', padding: '14px 24px', background: '#F8FAFC', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: '#64748B' }}>
            {t('scorecard.total_turns_count', { count: transcript.length }, `Total Dialogue Turns: ${transcript.length}`)}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="admin-btn admin-btn--secondary admin-btn--sm"
          >
            {t('scorecard.close_modal', undefined, 'Close')}
          </button>
        </div>
      </div>
    </div>
  );
};
